import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import useReveal from '../../AIEmployees/useReveal';
import { trackEvent, setNavSource } from '../../../utils/analytics';
import './A2AIntegrations.css';

/**
 * A2AIntegrations — where the platform is already available. Ties together the
 * existing OpenClaw / Linear / plugin surfaces into one story.
 *
 * Cards carry real brand marks (white chip + the platform's own logo) and are
 * ordered by real ecosystem traffic potential: OpenClaw + Linear first (our
 * live listing channels), then the plugin platforms, "your own agents" last.
 * Every click fires integration_card_click and sets nav_source so the
 * destination's page_view is attributed to this hub.
 */
/* TWO-FACE RULE (adaptive pages, Aug 23): `icon` is the DARK-face artwork
   (light/white marks). Where that mark vanishes on the light face, `iconLight`
   names the brand's dark-artwork twin; both imgs render and
   MarketingAdaptiveTheme.css's .mkt-face-* classes show exactly one per face.
   Full-colour marks (OpenClaw, Claude) carry no twin and always show. */
const INTS = [
  { name: 'OpenClaw', icon: '/assets/icons/agents/openclaw.svg', desc: 'The any-to-any execution layer for OpenClaw agents: one API key, every modality.', to: '/openclaw', status: 'Live' },
  { name: 'Linear', icon: '/assets/icons/linear.svg', iconLight: '/assets/icons/linear-light.svg', desc: 'Assign Linear issues to @CellCog: research, PDFs, dashboards delivered as native agent activities.', to: '/linear', status: 'Live' },
  { name: 'Muse', icon: '/assets/icons/agents/muse.png', desc: 'Your Meta Muse hands a brief to CellCog over MCP and brings the finished video, PDF, deck or research back into your Muse.', to: '/muse', status: 'Live' },
  { name: 'Claude Code', icon: '/assets/icons/agents/claude.png', desc: 'A CellCog plugin so your coding agent can generate research, media, and docs without leaving the IDE.', to: '/claude-code', status: 'Live' },
  { name: 'Cursor', icon: '/assets/icons/agents/cursor-dark.svg', iconLight: '/assets/icons/agents/cursor-light.svg', desc: 'Delegate creative, research, and document tasks to CellCog from inside Cursor.', to: '/cursor', status: 'Live' },
  { name: 'Codex', icon: '/assets/icons/agents/openai-light.svg', iconLight: '/assets/icons/agents/openai-dark.svg', desc: 'CellCog via the Open Plugins standard: research, media, and docs from Codex.', to: '/codex', status: 'Live' },
  { name: 'Hermes', icon: '/assets/icons/agents/hermes-light.png?v=7', iconLight: '/assets/icons/agents/hermes-dark.png', desc: 'Bring CellCog’s full platform to Hermes agents through the CellCog Python SDK.', to: '/hermes', status: 'Live' },
  { name: 'Your own agents', icon: null, desc: 'Any Open Plugins–conformant agent can call CellCog. Build on the same platform our agents use.', to: '#a2a-how', status: 'Live' },
];

const A2AIntegrations = () => {
  const [ref, visible] = useReveal();
  const navigate = useNavigate();
  const seenRef = React.useRef(false);
  React.useEffect(() => {
    if (visible && !seenRef.current) {
      seenRef.current = true;
      trackEvent('section_view', { section: 'integrations', page: 'agent-to-agent' });
    }
  }, [visible]);

  const go = (it) => {
    if (!it.to) return;
    trackEvent('integration_card_click', { platform: it.name, page: 'agent-to-agent' });
    // In-page anchor (e.g. "Your own agents" → the SDK example): scroll, don't route.
    if (it.to.startsWith('#')) {
      const t = document.getElementById(it.to.slice(1));
      if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // One-shot label consumed by the destination's page_view — makes the
    // hub → spoke flow visible in GA4 (nav_source: 'agent-to-agent').
    setNavSource('agent-to-agent');
    navigate(it.to);
  };

  return (
    <section className="a2a-int aie-section">
      <div className="aie-section-head">
        <h2 className="aie-section-title aie-display">Already open to your agents</h2>
        <p className="aie-section-sub">
          The platform ships where agents already work, and the catalog of
          skills keeps growing.
        </p>
      </div>
      <div className={`a2a-int__grid aie-reveal ${visible ? 'is-visible' : ''}`} ref={ref}>
        {INTS.map((it) => (
          <div
            className="a2a-int__card"
            key={it.name}
            role={it.to ? 'button' : undefined}
            tabIndex={it.to ? 0 : undefined}
            onClick={() => go(it)}
            onKeyDown={(e) => { if (it.to && e.key === 'Enter') go(it); }}
          >
            <span className="a2a-int__icon">
              {it.icon
                ? (
                  <>
                    <img
                      className={it.iconLight ? 'mkt-face-dark' : undefined}
                      src={it.icon}
                      alt={`${it.name} logo`}
                      loading="lazy"
                    />
                    {it.iconLight && (
                      <img className="mkt-face-light" src={it.iconLight} alt="" loading="lazy" />
                    )}
                  </>
                )
                : <span className="a2a-int__icon-glyph" aria-hidden="true">{'</>'}</span>}
            </span>
            <h3 className="a2a-int__name">{it.name}</h3>
            <p className="a2a-int__desc">{it.desc}</p>
            <span className={`a2a-int__status ${it.status !== 'Live' ? 'is-soon' : ''}`}>
              {it.status}{it.to && it.status === 'Live' ? <> &middot; Explore <ArrowRightOutlined /></> : null}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default A2AIntegrations;
